import React, { useEffect, useState, useContext } from 'react';
import axios from '../api/axiosConfig';
import Sidebar from '../components/Sidebar';
import Navbar from '../components/Navbar';
import CargarCUPLModal from '../components/CargarCUPLModal';
import DetalleTramiteModal from '../components/DetalleTramiteModal';
import Pagination from '../components/Pagination';
import NovedadesTramiteModal from '../components/NovedadesTramiteModal';
import * as XLSX from 'xlsx';
import { AuthContext } from '../context/AuthContext';
import './Profiles.css';

const BandejaTransito = () => {
  const { user } = useContext(AuthContext);
  const [tramites, setTramites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Estado para paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [soatLoading, setSoatLoading] = useState(false);
  const [soatMessage, setSoatMessage] = useState('');

  // Estados para modales
  const [selectedTramite, setSelectedTramite] = useState(null);
  const [showCUPLModal, setShowCUPLModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showPendingCuplModal, setShowPendingCuplModal] = useState(false);
  const [showNoveltyModal, setShowNoveltyModal] = useState(false);
  const [selectedTramiteForNovelty, setSelectedTramiteForNovelty] = useState(null);

  useEffect(() => {
    fetchTramites();
  }, []);

  const fetchTramites = async () => {
    try {
      setLoading(true);
      const res = await axios.get('/api/tramites');
      // Mostrar trámites que ya pasaron por validación interna y están en el tránsito
      setTramites(res.data.filter(t => ['radicado', 'pre_asignado', 'novedad_pendiente_soat', 'novedad_soat_cargado', 'novedad_soat_vigente', 'novedad_pendiente_cupl', 'novedad_cupl_generado', 'novedad_cupl_pagado', 'tramite_ejecutado'].includes(t.estado)));
      setError('');
    } catch (err) {
      setError('Error al cargar la bandeja de tránsito');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleVerificarSoats = async () => {
    setSoatLoading(true);
    setSoatMessage('');
    setError('');
    try {
      const res = await axios.post('/api/tramites/check-soats');
      setSoatMessage(res.data.message);
      setTimeout(() => setSoatMessage(''), 8000);
      fetchTramites();
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || 'Error al verificar SOATs automáticos.');
    } finally {
      setSoatLoading(false);
    }
  };

  const handleConfirmarPreasignado = async (id) => {
    if (!window.confirm('¿Confirmar la pre-asignación de este trámite?')) return;
    try {
      await axios.put(`/api/tramites/${id}/estado`, { estado: 'pre_asignado' });
      fetchTramites();
    } catch (err) {
      alert('Error al actualizar el estado del trámite');
    }
  };

  const handleEjecutarTramite = async (id) => {
    if (!window.confirm('¿Está seguro de finalizar y ejecutar este trámite? Esta acción es definitiva.')) return;
    try {
      await axios.put(`/api/tramites/${id}/estado`, { estado: 'tramite_ejecutado' });
      fetchTramites();
    } catch (err) {
      alert('Error al ejecutar el trámite');
    }
  };

  const handleEliminarTramite = async (id) => {
    try {
      await axios.delete(`/api/tramites/${id}`);
      fetchTramites();
    } catch (err) {
      alert(err.response?.data?.message || 'Error al eliminar el trámite');
    }
  };

  const handleOpenCUPL = (tramite) => {
    setSelectedTramite(tramite);
    setShowCUPLModal(true);
  };

  const handleOpenDetail = (tramite) => {
    setSelectedTramite(tramite);
    setShowDetailModal(true);
  };

  const handleOpenNovelty = (tramite) => {
    setSelectedTramiteForNovelty(tramite.id);
    setShowNoveltyModal(true);
  };

  // Lógica de paginación
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = tramites.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (items) => {
    setItemsPerPage(items);
    setCurrentPage(1);
  };

  const getEstadoLabel = (estado) => {
    const labels = {
      'radicado': 'Radicado',
      'pre_asignado': 'Pre-Asignado',
      'novedad_pendiente_soat': 'SOAT Pendiente',
      'novedad_soat_cargado': 'SOAT Cargado',
      'novedad_soat_vigente': 'SOAT Vigente',
      'novedad_pendiente_cupl': 'CUPL Pendiente',
      'novedad_cupl_generado': 'CUPL Generado',
      'novedad_cupl_pagado': 'CUPL Pagado',
      'tramite_ejecutado': 'Trámite Ejecutado'
    };
    return labels[estado] || estado;
  };

  const handleDownloadExcel = () => {
    if (tramites.length === 0) {
      alert('No hay trámites para descargar.');
      return;
    }

    const data = tramites.map(t => {
      const v = t.datos_adicionales?.vehicleDetails?.vehiculo;
      const cc = v?.cilindraje || v?.datos_completos?.rna?.datos?.cilindraje;
      return {
        'ID': t.id,
        'Tipo de Trámite': t.tramite_type_name || '',
        'Propietario': t.datos_adicionales?.propietario || '',
        'Placa': t.datos_adicionales?.placa_referencia || '',
        'Cilindraje': cc ? `${cc} cc` : '',
        'Tránsito': t.transito_name || '',
        'Estado': getEstadoLabel(t.estado),
        'Fecha de Creación': t.created_at ? new Date(t.created_at).toLocaleDateString('es-CO') : ''
      };
    });

    const ws = XLSX.utils.json_to_sheet(data);

    // Auto-ajustar ancho de columnas
    const colWidths = Object.keys(data[0]).map(key => ({
      wch: Math.max(key.length, ...data.map(row => String(row[key] ?? '').length)) + 2
    }));
    ws['!cols'] = colWidths;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Bandeja Tránsito');
    XLSX.writeFile(wb, `bandeja_transito_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  return (
    <div className="app-layout">
      <Sidebar />

      <div className="main-content">
        <Navbar title="📥 Bandeja de Entrada Tránsito" />

        <div className="page-content">
          {error && <div className="error-banner">{error}</div>}
          {soatMessage && (
            <div style={{ padding: '15px', backgroundColor: '#dcfce7', color: '#166534', borderRadius: '8px', marginBottom: '20px', border: '1px solid #bbf7d0', fontWeight: 'bold' }}>
              {soatMessage}
            </div>
          )}

          <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
            <div className="header-title">
              <h2>Trámites en Curso</h2>
              <p className="subtitle">Gestión de trámites para la generación de CUPL y pre-asignación</p>
            </div>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>

              <button 
                className="btn-action" 
                onClick={() => setShowPendingCuplModal(true)}
                style={{ 
                  background: 'var(--warning-soft, #fef3c7)', 
                  color: 'var(--warning-color, #d97706)', 
                  border: '1px solid rgba(217, 119, 6, 0.2)', 
                  padding: '10px 16px', 
                  borderRadius: '8px', 
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                }}
              >
                📋 Pendientes de CUPL ({tramites.filter(t => t.estado === 'novedad_pendiente_cupl').length})
              </button>
              <button 
                className="btn-action" 
                onClick={handleVerificarSoats} 
                disabled={soatLoading}
                style={{ 
                  background: 'var(--primary-color, #2563eb)', 
                  color: 'white', 
                  border: 'none', 
                  padding: '10px 16px', 
                  borderRadius: '8px', 
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}
              >
                {soatLoading ? '⏳ Verificando...' : '🛡️ Verificar SOATs automáticos'}
              </button>
            </div>
          </div>

          {loading ? (
            <p className="loading">Cargando bandeja...</p>
          ) : (
            <div className="table-wrapper">
              <table className="users-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Tipo</th>
                    <th>Propietario</th>
                    <th>Placa</th>
                    <th>Cilindraje</th>
                    <th>Tránsito</th>
                    <th>Estado</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.map(t => (
                    <tr key={t.id}>
                      <td><strong>#{t.id}</strong></td>
                      <td>{t.tramite_type_name}</td>
                      <td>{t.datos_adicionales?.propietario || 'N/A'}</td>
                      <td>
                        {t.datos_adicionales?.placa_referencia ? (
                          <span className="plate-badge-sm">{t.datos_adicionales.placa_referencia}</span>
                        ) : '—'}
                      </td>
                      <td>
                        {(() => {
                          const v = t.datos_adicionales?.vehicleDetails?.vehiculo;
                          const cc = v?.cilindraje || v?.datos_completos?.rna?.datos?.cilindraje;
                          return cc ? `${cc} cc` : '—';
                        })()}
                      </td>
                      <td>
                        <span style={{ background: '#e0e7ff', color: '#3730a3', padding: '2px 10px', borderRadius: '6px', fontWeight: '600', fontSize: '12px', whiteSpace: 'nowrap' }}>
                          {t.transito_name || '—'}
                        </span>
                      </td>
                      <td>
                        <span className={`status ${
                          t.estado === 'radicado' ? 'pending' :
                          t.estado === 'pre_asignado' ? 'activo' : 
                          t.estado === 'novedad_soat_vigente' ? 'paid' : 
                          t.estado === 'novedad_cupl_pagado' ? 'paid' :
                          t.estado === 'tramite_ejecutado' ? 'paid' :
                          'warning'
                        }`}>
                          {t.estado === 'radicado' ? '📝 Radicado' :
                           t.estado === 'pre_asignado' ? 'Pre-Asignado' : 
                           t.estado === 'novedad_pendiente_soat' ? 'SOAT Pendiente' : 
                           t.estado === 'novedad_soat_cargado' ? 'SOAT Cargado' :
                           t.estado === 'novedad_soat_vigente' ? 'SOAT Vigente' :
                           t.estado === 'novedad_pendiente_cupl' ? 'CUPL Pendiente' :
                           t.estado === 'novedad_cupl_generado' ? 'CUPL Generado' :
                           t.estado === 'novedad_cupl_pagado' ? 'CUPL Pagado' :
                           t.estado === 'tramite_ejecutado' ? 'Trámite Ejecutado' :
                           t.estado}
                        </span>
                        {t.pending_novelties_count > 0 && (
                          <div style={{ marginTop: '5px' }}>
                            <span style={{
                              padding: '2px 6px',
                              borderRadius: '4px',
                              fontSize: '10px',
                              fontWeight: '900',
                              background: '#fee2e2',
                              color: '#dc2626',
                              border: '1px solid rgba(220, 38, 38, 0.2)',
                              whiteSpace: 'nowrap'
                            }} title={`Este trámite tiene ${t.pending_novelties_count} novedades pendientes`}>
                              ⚠️ NOVEDAD ({t.pending_novelties_count})
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="actions">
                        <button 
                          className="btn-action btn-view" 
                          onClick={() => handleOpenDetail(t)}
                        >
                          👁️ Ver
                        </button>
                        
                        <button 
                          className="btn-action" 
                          style={{
                            background: t.pending_novelties_count > 0 ? 'rgba(239, 68, 68, 0.1)' : 'rgba(100, 116, 139, 0.1)',
                            color: t.pending_novelties_count > 0 ? '#dc2626' : '#64748b',
                            border: t.pending_novelties_count > 0 ? '1px solid rgba(239, 68, 68, 0.2)' : '1px solid rgba(100, 116, 139, 0.2)',
                            fontWeight: '600'
                          }}
                          onClick={() => handleOpenNovelty(t)}
                        >
                          ⚠️ Novedades {t.pending_novelties_count > 0 && `(${t.pending_novelties_count})`}
                        </button>
                        
                        {(!t.datos_adicionales?.cupl_cargado && t.pre_assigned_plate_id && t.estado !== 'tramite_ejecutado' && t.estado !== 'rechazado') && (
                          <button 
                            className="btn-action btn-modules" 
                            onClick={() => handleOpenCUPL(t)}
                          >
                            📄 CUPL
                          </button>
                        )}

                        {t.estado === 'radicado' && (
                          <button 
                            className="btn-action" 
                            style={{ background: 'var(--success-soft)', color: 'var(--success-color)', border: '1px solid var(--success-color)' }}
                            onClick={() => handleConfirmarPreasignado(t.id)}
                          >
                            ✅ Pre-asignar
                          </button>
                        )}

                        {(t.datos_adicionales?.soat_aprobado === true && t.datos_adicionales?.cupl_pagado === true && t.estado !== 'tramite_ejecutado') && (
                          <button 
                            className="btn-action" 
                            style={{ 
                              background: t.pending_novelties_count > 0 ? '#f1f5f9' : 'var(--primary-soft)', 
                              color: t.pending_novelties_count > 0 ? '#94a3b8' : 'var(--primary-color)', 
                              border: t.pending_novelties_count > 0 ? '1px solid #cbd5e1' : '1px solid var(--primary-color)',
                              cursor: t.pending_novelties_count > 0 ? 'not-allowed' : 'pointer'
                            }}
                            disabled={t.pending_novelties_count > 0}
                            title={t.pending_novelties_count > 0 ? "No se puede finalizar: tiene novedades pendientes de solución" : "Finalizar y ejecutar trámite"}
                            onClick={() => handleEjecutarTramite(t.id)}
                          >
                            🔒 Finalizar
                          </button>
                        )}
                        {user?.role === 'superadmin' && (
                          <button 
                            className="btn-action" 
                            onClick={() => {
                              if (window.confirm('¿Está seguro de eliminar este trámite de forma permanente?')) {
                                handleEliminarTramite(t.id);
                              }
                            }}
                            style={{ 
                              background: 'rgba(239, 68, 68, 0.1)', 
                              color: '#ef4444', 
                              border: '1px solid rgba(239, 68, 68, 0.2)',
                              padding: '6px 10px',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              marginLeft: '5px',
                              transition: 'all 0.2s'
                            }}
                            title="Eliminar Trámite"
                          >
                            🗑️
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {tramites.length === 0 && (
                <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
                  No hay trámites pendientes en esta bandeja.
                </div>
              )}
              
              <Pagination 
                currentPage={currentPage}
                totalItems={tramites.length}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
              />
            </div>
          )}
        </div>
      </div>

      <CargarCUPLModal 
        isOpen={showCUPLModal} 
        onClose={() => setShowCUPLModal(false)} 
        tramite={selectedTramite} 
        onSuccess={fetchTramites} 
      />

      <DetalleTramiteModal 
        isOpen={showDetailModal} 
        onClose={() => setShowDetailModal(false)} 
        tramiteId={selectedTramite?.id} 
        onUpdate={fetchTramites} 
      />

      <NovedadesTramiteModal 
        isOpen={showNoveltyModal} 
        tramiteId={selectedTramiteForNovelty} 
        onClose={() => {
          setShowNoveltyModal(false);
          setSelectedTramiteForNovelty(null);
        }} 
        onSuccess={fetchTramites} 
      />

      {showPendingCuplModal && (
        <div className="modal-overlay" onClick={() => setShowPendingCuplModal(false)}>
          <div className="modal-content modal-small" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '15px', borderBottom: '1px solid #e2e8f0' }}>
              <h3 style={{ margin: 0 }}>📋 Trámites Pendientes de CUPL</h3>
              <button 
                onClick={() => setShowPendingCuplModal(false)}
                style={{ border: 'none', background: 'none', fontSize: '20px', cursor: 'pointer', color: '#64748b' }}
              >
                &times;
              </button>
            </div>
            <div className="modal-body" style={{ padding: '20px 0' }}>
              {tramites.filter(t => t.estado === 'novedad_pendiente_cupl').length === 0 ? (
                <p style={{ textAlign: 'center', color: '#64748b', fontStyle: 'italic', margin: '20px 0' }}>
                  No hay trámites pendientes de CUPL.
                </p>
              ) : (
                <div style={{ maxHeight: '350px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                    <thead>
                      <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                        <th style={{ padding: '12px 16px', fontWeight: 'bold', fontSize: '13px', color: '#475569' }}>Placa</th>
                        <th style={{ padding: '12px 16px', fontWeight: 'bold', fontSize: '13px', color: '#475569' }}>Tipo de Trámite</th>
                        <th style={{ padding: '12px 16px', fontWeight: 'bold', fontSize: '13px', color: '#475569' }}>Tránsito</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tramites.filter(t => t.estado === 'novedad_pendiente_cupl').map(t => (
                        <tr key={t.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '12px 16px' }}>
                            {t.datos_adicionales?.placa_referencia ? (
                              <span className="plate-badge-sm">{t.datos_adicionales.placa_referencia}</span>
                            ) : (
                              <span style={{ color: '#94a3b8' }}>—</span>
                            )}
                          </td>
                          <td style={{ padding: '12px 16px', fontSize: '14px', color: '#334155' }}>
                            {t.tramite_type_name}
                          </td>
                          <td style={{ padding: '12px 16px', fontSize: '13px' }}>
                            <span style={{ background: '#e0e7ff', color: '#3730a3', padding: '2px 8px', borderRadius: '6px', fontWeight: '600' }}>
                              {t.transito_name || '—'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="modal-actions" style={{ borderTop: '1px solid #e2e8f0', paddingTop: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <button 
                onClick={handleDownloadExcel}
                style={{ 
                  background: '#dcfce7', 
                  color: '#16a34a', 
                  border: '1px solid rgba(22, 163, 74, 0.2)', 
                  padding: '8px 16px', 
                  borderRadius: '6px', 
                  cursor: 'pointer',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '14px'
                }}
              >
                📥 Descargar Excel
              </button>
              <button 
                className="btn-secondary" 
                onClick={() => setShowPendingCuplModal(false)}
                style={{ padding: '8px 16px', borderRadius: '6px', fontWeight: '600' }}
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BandejaTransito;
